/* eslint-disable import/prefer-default-export */
import React from 'react';
import {
  TableCellProps,
  TableFooterProps,
  TableColumn,
  TableDatum,
  CustomTooltipInfo,
} from '@quality24/design-system';
import { get } from 'lodash';
import { UseExpandedRowProps } from '@quality24/design-system/src/components/organisms/DataTable/GroupedRowsDataTable';
import {
  TableComponentData,
  TableCellRender,
  TableGroupByInterface,
  Customizations,
} from '../../../../@types';

import CustomCell from './CustomCell';
import { aggregateFooter } from '../../utils/aggregation';
import { ReferenceMap } from '../../utils/reference';
import { sanitizeKey, formatValue } from '../../../../utils/dataUtils';
import { TimeUnit, convertTime } from '../../../../utils/momentUtils';
import CustomFooter from './CustomFooter';

/**
 * Apply a post processing for basic math operations on aggregated/mapped data.
 * Especially useful when we cannot do the intended operation during aggregation.
 * @param value the value to which we'll apply the post processing
 * @param row the table row from which we'll get the ref value
 * @param group the group configuration containing the postProcessing data
 */
export const applyPostProcessing = (
  value: number,
  row: TableCellProps<TableDatum>['cell']['row']['original'],
  {
    key: groupKey,
    cellFormatter,
    postProcessing,
  }: Pick<TableGroupByInterface, 'key' | 'cellFormatter' | 'postProcessing'>,
): number => {
  if (!postProcessing) return value;

  // Sibling/denominator lookup. Subgroup columns are keyed `${parent}-${sub}`,
  // so a sibling lives at `${parent}-${postProcessing.key}`. Top-level columns
  // have no parent prefix, so fall back to the plain `${postProcessing.key}`.
  const [prefix] = groupKey.split('-');
  const cellValue = value || 0;
  const refValue = get(
    row,
    `${prefix}-${postProcessing.key}`,
    get(row, postProcessing.key, 0),
  ) as number;

  // Divide yields a unitless ratio (pair with a `percentage` cellFormatter to
  // display it as a %), so it applies regardless of the duration formatting.
  if (postProcessing.operation === 'divide') {
    return refValue ? cellValue / refValue : 0;
  }

  if (cellFormatter?.type === 'duration') {
    const cValue = convertTime(cellValue, cellFormatter?.unit as TimeUnit);
    const rValue = convertTime(refValue, postProcessing.unit);

    if (postProcessing.operation === 'sum') {
      return convertTime(
        cValue + rValue,
        'milliseconds',
        cellFormatter?.unit as TimeUnit,
      );
    }
    if (postProcessing.operation === 'subtract') {
      return convertTime(
        cValue - rValue,
        'milliseconds',
        cellFormatter?.unit as TimeUnit,
      );
    }
  }

  if (postProcessing.operation === 'sum') {
    return value + refValue;
  }
  if (postProcessing.operation === 'subtract') {
    return value - refValue;
  }

  return value;
};

//
// Column parser
//

const buildColumnDefinition = (
  group: TableComponentData['groupBy'][0],
  options: TableComponentData['options'],
  refLabel?: string,
  customCellComponents?: Customizations['customCellComponents'],
) => {
  const key = refLabel || sanitizeKey(group.key);
  const cell: TableColumn<TableDatum> = {
    id: key,
    Header: () => (
      <span className="d-flex">
        {refLabel || group.label}
        {group.tooltipContent && (
          <CustomTooltipInfo info={group.tooltipContent} />
        )}
      </span>
    ),
    accessor: key,
    disableSortBy: group.sortable !== true,
    // disableResizing: !group.resizable,
    ...group.options,
  };

  if (group.cellFormatter && !group.cellRender) {
    cell.Cell = ({ value, cell: { row } }: TableCellProps<TableDatum>) => (
      <>
        {formatValue(
          group.postProcessing
            ? applyPostProcessing(value, row.original, {
                ...group,
                key,
              })
            : value,
          group.cellFormatter,
          group.mapping,
        )}
      </>
    );
  }

  // Custom cell rendering
  if (group.cellRender) {
    cell.Cell = (props: TableCellProps<TableDatum>) => {
      const value = group.postProcessing
        ? applyPostProcessing(props.value, props.cell.row.original, {
            ...group,
            key,
          })
        : props.value;

      if (
        ((props.cell.row as unknown as UseExpandedRowProps<TableDatum>)
          .canExpand &&
          group.cellRender?.renderOnRowType === 'subRow') ||
        (!(props.cell.row as unknown as UseExpandedRowProps<TableDatum>)
          .canExpand &&
          group.cellRender?.renderOnRowType === 'row')
      ) {
        return <>-</>;
      }

      return (
        <CustomCell
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          value={value}
          formattedValue={formatValue(
            value,
            group.cellFormatter,
            group.mapping,
          )}
          config={group.cellRender as TableCellRender}
          group={{ ...group, key }}
          customCellComponents={customCellComponents}
        />
      );
    };
  }

  // Footer config
  if (options?.footer) {
    cell.Footer = (props: TableFooterProps<TableDatum>) => {
      if (group.footerLabel) return <span>{group.footerLabel}</span>;

      if (group.aggregation && group.cellRender?.renderOnFooter !== false) {
        // When the whole column is empty (only placeholder cells, no numeric
        // values), surface the column's defaultValue instead of the 0 that an
        // empty aggregateFooter sum/avg returns. A genuine 0 counts as data.
        if (
          group.defaultValue !== undefined &&
          !props.rows.some(
            (r) => r.depth === 0 && Number.isFinite(Number(r.values[key])),
          )
        ) {
          return <span>{group.defaultValue}</span>;
        }

        // find values pre-formatting
        const value = aggregateFooter(
          props.rows
            .filter((r) => r.depth === 0 && !Number.isNaN(r.values[key]))
            .map((r) => r.values)
            .map((row) => {
              if (group.postProcessing) {
                return {
                  ...row,
                  [key]: applyPostProcessing(row[key], row, {
                    ...group,
                    key,
                  }),
                };
              }

              return row;
            }),
          key,
          group.aggregation,
        );

        return group.cellRender?.renderOnFooter ? (
          <CustomFooter
            value={value}
            formattedValue={formatValue(
              value,
              group.cellFormatter,
              group.mapping,
            )}
            config={group.cellRender as TableCellRender}
            mapping={group.mapping}
            customCellComponents={customCellComponents}
          />
        ) : (
          <>{formatValue(value, group.cellFormatter, group.mapping)}</>
        );
      }
      return <>-</>;
    };
  }

  return cell;
};

/**
 * Create grouped columns from group definition
 * @param column
 * @param group
 * @param options
 * @returns
 */
const buildGroupedColumns = (
  column: TableColumn<TableDatum>,
  group: TableGroupByInterface,
  options: TableComponentData['options'],
  customCellComponents?: Customizations['customCellComponents'],
): TableColumn<TableDatum> => {
  const { Footer, Header, ...baseColumn } = column;
  const hasSubgroups = group.subGroups && group.subGroups.length > 0;
  return {
    ...baseColumn,
    Header: hasSubgroups ? Header : ' ',
    disableSortBy: true, // never sort by parent group
    ...group.options,
    columns:
      group.subGroups && group.subGroups.length > 0
        ? group.subGroups
            .filter((g) => g.visible !== false) // ignore if column is no visible
            .map((sg) => ({
              ...buildColumnDefinition(
                sg,
                options,
                `${baseColumn.id}-${sanitizeKey(sg.key)}`,
                customCellComponents,
              ),
              Header: sg.label,
            }))
        : [column],
  } as TableColumn<TableDatum>;
};

/**
 * Parses the groupBy data into a column definition
 * @param groupBy
 * @param reference
 * @param options
 * @returns
 */
export const parseColumns = (
  groupBy: TableComponentData['groupBy'],
  reference: ReferenceMap,
  options: TableComponentData['options'],
  customCellComponents?: Customizations['customCellComponents'],
): Array<TableColumn<TableDatum>> => {
  // Check if we have any grouped groupBy. We need a different structure if we have
  // at least one subgroup.
  const hasGroupedColumns = groupBy.some(
    (g) => g.subGroups && g.subGroups?.length > 0,
  );

  return groupBy
    .filter((g) => g.visible !== false) // ignore if column is no visible
    .flatMap((g) => {
      if (reference && g.reference) {
        const refs = reference[g.reference.id];
        if (!refs) return [];

        return refs.map((ref) => {
          const baseColumn = buildColumnDefinition(
            g,
            options,
            ref.label,
            customCellComponents,
          );

          return hasGroupedColumns
            ? buildGroupedColumns(baseColumn, g, options, customCellComponents)
            : baseColumn;
        });
      }

      const baseColumn = buildColumnDefinition(
        g,
        options,
        undefined,
        customCellComponents,
      );

      return hasGroupedColumns
        ? buildGroupedColumns(baseColumn, g, options, customCellComponents)
        : baseColumn;
    });
};
